/**
 * The whole right-hand panel for a plan that runs at the client's own pace — the same job
 * `projectionGrid.ts` does for a dated plan, and a separate module for the same reason
 * `tapeProjection` sits beside `weekProjection` rather than branching it.
 *
 * ── Why not one projection with a mode flag ──────────────────────────────────────────────────
 * They share nothing but the words "cycle" and "session". The dated projection's every field is a
 * date or derived from one — the chip's date, the cell it lands in, the pattern date a move can be
 * restored to, the week the rail names, the Stationary time the generator will book — and here
 * there is not one date in the whole projection, because there is not one date in the plan. Branching would make
 * every field of the result conditionally meaningful, which is the shape that makes a caller read
 * both halves to trust either.
 *
 * ── What replaces the date, and why it is a POSITION ─────────────────────────────────────────
 * `self-paced-plan-requirements.md` R8: the calendar "MUST NOT place any dated row on any calendar
 * for an occurrence that has not been lived". R13: the assignment takes no scheduling input. So the
 * only true answer to "when is this workout" is "after the one before it", and position is what says
 * that. The trainer reads the ORDER they authored — the same order the cursor (R3) will walk.
 *
 * R12 is why every cycle is enumerated rather than one cycle being shown with "× 4" beside it: the
 * whole plan is readable, and a per-cycle lifespan (R18) can make cycle three hold different days
 * from cycle one. Drawing the phase once would hide exactly the cases the trainer needs to check.
 *
 * That claim was false for as long as it stood here: `planCycleSlots` handed every cycle the same
 * day array, so the enumeration drew four identical copies of one phase and the sentence above
 * described a capability the list did not have. The filter lives in `planCycleSlots` — the single
 * cycle-numbering authority — so this projection, the dated one and the session count below all
 * read one answer rather than three.
 *
 * ── The rest note is advisory and says so ────────────────────────────────────────────────────
 * R2: a day's rest days "MUST NOT be summed into a cycle length, and MUST NOT be used to derive the
 * date of any occurrence". Nothing here adds them up or turns one into a date; each is shown against
 * the day it was authored on, which is where R9 gives it its real meaning — a window measured from
 * the client's own finish timestamp, which this dialog never holds.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` arrives as a parameter.
 */

import { planLengthLabel } from '~/entities/plan-draft';

import { coordinateKey, planCycleSlots, type ScheduleTree } from './assignSchedule';
import type { Translate } from './assignRefusalCopy';

/** One occurrence, as the dateless review renders it. */
export interface SelfPacedReviewSession {
  /** The coordinate key — the row's identity, the same shape the dated review uses. */
  readonly key: string;
  /** The workout's name, or the day's, or a positional fallback. Always non-empty. */
  readonly title: string;
  /**
   * "sesja 2 z 2" on a training day holding more than one, `null` on a day holding one. R3 puts the
   * cursor on an OCCURRENCE, not a day, so a two-session day is trained as two turns — and a list
   * that showed them as one row would misdescribe what the client is about to be given.
   */
  readonly sessionLabel: string | null;
}

/** One training day of one cycle. */
export interface SelfPacedReviewDay {
  readonly key: string;
  /** 1-based position in the cycle. The date's replacement — see the header. */
  readonly position: number;
  /** The authored day name, or a positional fallback. Always non-empty. */
  readonly title: string;
  /** "potem 2 dni przerwy", or `null` when the trainer prescribed none. Advisory (R2). */
  readonly restNote: string | null;
  readonly sessions: readonly SelfPacedReviewSession[];
}

/** One cycle's block. */
export interface SelfPacedReviewCycle {
  readonly key: string;
  /** "Cykl 3" — unit noun interpolated, never baked into the key. */
  readonly heading: string;
  /** "Fundament · cykl 3 w tej fazie" — where this cycle sits in the plan. */
  readonly phaseCaption: string;
  readonly days: readonly SelfPacedReviewDay[];
}

/** The step's copy. */
export interface SelfPacedReview {
  readonly cycles: readonly SelfPacedReviewCycle[];
  /** "4 cykle · 12 sesji" — the one-line summary above the list. */
  readonly summary: string;
  /**
   * What this mode promises, in the trainer's own words: the plan waits for the client, and no date
   * is booked anywhere. Said on the review rather than on a step of its own, because this is the
   * screen where the trainer would otherwise be looking for the dates.
   */
  readonly promise: string;
  readonly totalOccurrences: number;
  /** Said when the plan generates nothing at all. `null` otherwise. */
  readonly emptyMessage: string | null;
}

/** The plan's counting unit, as a noun to interpolate. */
function unitNoun(t: Translate): string {
  return t('web:assign.unit.cycle');
}

/** The recovery note after a day, or `null` when there is none to state. */
function restNoteOf(restDaysAfter: number | null, t: Translate): string | null {
  if (restDaysAfter === null || restDaysAfter <= 0) return null;
  return t('web:assign.selfPaced.restNote', { count: restDaysAfter });
}

interface SelfPacedReviewInput {
  readonly tree: ScheduleTree;
  /** Only for casing the interpolated unit noun — Polish lower-cases it mid-sentence. */
  readonly locale: string;
  readonly t: Translate;
}

export function selfPacedReview(input: SelfPacedReviewInput): SelfPacedReview {
  const { tree, locale, t } = input;
  let totalOccurrences = 0;

  const cycles: SelfPacedReviewCycle[] = planCycleSlots(tree).map((slot) => {
    // A day carrying no session generates no occurrence, in either mode — the dated walk drops it
    // by iterating sessions, and dropping it here explicitly keeps the two lists the same length.
    const trainingDays = slot.days.filter((day) => day.sessions.length > 0);

    const days: SelfPacedReviewDay[] = trainingDays.map((day, offset) => {
      const position = offset + 1;
      const dayTitle = day.name?.trim()
        ? day.name.trim()
        : t('web:assign.live.untitledSession', { n: position });
      const ordered = [...day.sessions].sort((a, b) => a.order - b.order);

      return {
        key: `${String(slot.cycleIndex)}:${String(slot.phaseOrder)}:${String(day.order)}`,
        position,
        title: dayTitle,
        restNote: restNoteOf(day.restDaysAfter, t),
        sessions: ordered.map((session, sessionOffset) => {
          totalOccurrences += 1;
          const name = session.name.trim();
          return {
            key: coordinateKey({
              cycleIndex: slot.cycleIndex,
              phaseOrder: slot.phaseOrder,
              dayOrder: day.order,
              sessionOrder: session.order,
            }),
            title: name === '' ? dayTitle : name,
            sessionLabel:
              ordered.length > 1
                ? t('web:assign.selfPaced.sessionOf', {
                    current: sessionOffset + 1,
                    total: ordered.length,
                  })
                : null,
          };
        }),
      };
    });

    return {
      key: `${String(slot.phaseOrder)}:${String(slot.cycleIndex)}`,
      heading: t('web:assign.selfPaced.cycleHeading', {
        unit: unitNoun(t),
        n: slot.cycleIndex,
      }),
      phaseCaption: t('web:assign.selfPaced.phaseCaption', {
        phase: slot.phaseName,
        current: slot.cycleInPhase,
        unit: unitNoun(t).toLocaleLowerCase(locale),
      }),
      days,
    };
  });

  return {
    cycles,
    summary: t('web:assign.selfPaced.summary', {
      units: planLengthLabel('selfpaced', cycles.length, t),
      sessions: t('web:assign.selfPaced.sessions', { count: totalOccurrences }),
    }),
    promise: t('web:assign.selfPaced.promise'),
    totalOccurrences,
    emptyMessage: totalOccurrences === 0 ? t('web:assign.live.empty') : null,
  };
}
